import React from "react";
import { ScrollView, StyleSheet } from "react-native";
import { PdStack, PdText } from "src/components/layout";
import { PdModal, PdModalCloseButton, PdModalTitle } from "src/components/overlay";
import { getApiError, postReferralInvite, postReferrals } from "src/util/apiNew";

import { kStyleConst } from "src/kStyle";
import { tModalComponentProps } from "src/redux/modals/types";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { useIsMobile } from "../ui";
import { tUseModal, useModal } from "src/providers/OverlayProvider";
import { useToast } from "../toast";
import { PdInput } from "src/components/forms/input";
import { kColors } from "src/kColors";

type tReferCompaniesModal = {
  type: "brand" | "rep";
  defaultEmailsList?: string;
  referralId?: string;
};

const DEFAULT_SEND_NOTE_MESSAGE = `Hi there,

I'm reaching out to invite you to join The Main Tab, the only luxury wholesale marketplace allowing brands, retailers, and sales reps to work together in one main platform. 

I use themaintab.com for my wholesale buying and discovery and would love to have your brand available here. 

Use my link below to apply and join. Look forward to seeing you here!

Thank you,`;

const DEFAULT_RESEND_NOTE_MESSAGE = `Hi there,

I'm reaching out again to invite you to join The Main Tab, the only luxury wholesale marketplace allowing brands, retailers, and sales reps to work together in one main platform.

I use themaintab.com for my wholesale buying and discovery and would love to have your brand available here.

Use my link below to apply and join. Look forward to seeing you here!

Thank you,`;

const ModalFeedback: React.FC<{ hasExistingUsers: boolean }> = ({ hasExistingUsers }) => {
  return (
    <ScrollView contentContainerStyle={styles.modalBody}>
      <PdModalTitle style={styles.title}>Invites sent!</PdModalTitle>
      <PdText>Your invitations have been sent and you can view the status of the brand invites below.</PdText>
      {hasExistingUsers && (
        <PdText style={styles.mt3}>
          {`One or more e-mail address you invited has an active brand account on The Main Tab. We have automatically connected you to this brand so you can shop them on our marketplace. You can view all your brands in the "My Brands" section.`}
        </PdText>
      )}
    </ScrollView>
  );
};

const ModalContent: React.FC<
  tReferCompaniesModal & {
    isSending?: boolean;
    onSend?: (params: { emailsList: string; note: string }) => void;
    onClose?: () => void;
  }
> = ({ type = "brand", defaultEmailsList = "", referralId, isSending, onSend, onClose }) => {
  const rIsMobile = useIsMobile();
  const [emailsList, setEmailsList] = React.useState(defaultEmailsList);
  const [note, setNote] = React.useState(!referralId ? DEFAULT_SEND_NOTE_MESSAGE : DEFAULT_RESEND_NOTE_MESSAGE);

  const isValid = !!emailsList && !!note;

  const handleOnSend = () => {
    onSend?.({ emailsList, note });
  };

  return (
    <ScrollView contentContainerStyle={styles.modalBody}>
      <PdModalTitle style={styles.title}>{`Message to your ${
        type === "brand" ? "Brands" : "Sales Reps"
      }`}</PdModalTitle>
      <PdInput
        value={emailsList}
        onChangeText={setEmailsList}
        isDisabled={!!referralId}
        placeholder="jane@email.com, john@email.com"
        style={styles.mb3}
      />
      <PdInput multiline numberOfLines={10} value={note} onChangeText={setNote} placeholder="Note" style={styles.mb3} />
      <PdStack direction={rIsMobile ? "vertical" : "horizontal"} isReversed={rIsMobile} layout={[1, 1]} gap={3}>
        <PdButton colorScheme="purple" isDisabled={isSending} onPress={onClose} variant="outline">
          <PdButtonLabel>Cancel</PdButtonLabel>
        </PdButton>
        <PdButton
          isLoading={isSending}
          isDisabled={!isValid}
          onPress={handleOnSend}
          style={rIsMobile ? styles.mb4 : {}}
        >
          <PdButtonLabel>Send</PdButtonLabel>
        </PdButton>
      </PdStack>
    </ScrollView>
  );
};

const ReferCompaniesModal: React.FC<tModalComponentProps<tReferCompaniesModal>> = ({
  type = "brand",
  defaultEmailsList = "",
  referralId,
  onClose,
}) => {
  const toast = useToast();
  const [isSending, setSending] = React.useState(false);
  const [view, setView] = React.useState<"form" | "success-invited" | "success-invited-existing">("form");

  const handleOnSend = async ({ emailsList, note }: { emailsList: string; note: string }) => {
    try {
      setSending(true);
      if (referralId) {
        await postReferralInvite(referralId, { type, note });
        setView("success-invited");
      } else {
        const emails = emailsList.split(",");
        const result = await postReferrals({ emails, type, note });
        setView(result.hasExistingUsers ? "success-invited-existing" : "success-invited");
      }
    } catch (error) {
      toast({ description: getApiError(error).message, status: "error" });
    } finally {
      setSending(false);
    }
  };

  const handleOnClose = () => {
    onClose();
  };

  return (
    <PdModal style={styles.modal} closeOnOverlayClick={!isSending} onClose={handleOnClose}>
      <PdModalCloseButton isDisabled={isSending} onPress={handleOnClose} />
      {view === "form" ? (
        <ModalContent
          type={type}
          defaultEmailsList={defaultEmailsList}
          referralId={referralId}
          isSending={isSending}
          onSend={handleOnSend}
          onClose={handleOnClose}
        />
      ) : (
        <ModalFeedback hasExistingUsers={view === "success-invited-existing"} />
      )}
    </PdModal>
  );
};

const useReferCompaniesModal = (): tUseModal<tReferCompaniesModal> => {
  return useModal<tReferCompaniesModal>(ReferCompaniesModal);
};

export default useReferCompaniesModal;

const styles = StyleSheet.create({
  mb3: {
    marginBottom: 3 * kStyleConst.kSpacing,
  },
  mb4: {
    marginBottom: 4 * kStyleConst.kSpacing,
  },
  modal: {
    marginHorizontal: 8 * kStyleConst.kSpacing,
    maxWidth: 600,
    width: "100%",
  },
  modalBody: {
    backgroundColor: kColors.lightBrand,
    paddingHorizontal: 6 * kStyleConst.kSpacing,
    paddingVertical: 8 * kStyleConst.kSpacing,
  },
  mt3: {
    marginTop: 3 * kStyleConst.kSpacing,
  },
  title: {
    marginBottom: 4 * kStyleConst.kSpacing,
  },
});
